import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Ship, ArrowRight, MapPin } from 'lucide-react';

const TradeRoutes = () => {
  const routes = [
    {
      origin: 'Europe',
      destination: 'South Africa',
      description: 'Established freight routes connecting major European hubs with South African ports and airports.',
      airRoutes: [
        { from: 'Paris (CDG)', to: 'Johannesburg (JNB)', transit: '11-13 hours' },
        { from: 'Frankfurt (FRA)', to: 'Johannesburg (JNB)', transit: '10-12 hours' },
        { from: 'Amsterdam (AMS)', to: 'Cape Town (CPT)', transit: '11-13 hours' }
      ],
      oceanRoutes: [
        { from: 'Le Havre', to: 'Durban', transit: '18-22 days' },
        { from: 'Marseille', to: 'Cape Town', transit: '16-20 days' },
        { from: 'Rotterdam', to: 'Ngqura', transit: '18-22 days' }
      ]
    },
    {
      origin: 'South Africa',
      destination: 'Europe',
      description: 'Reliable export routes from South Africa to European markets for commercial cargo.',
      airRoutes: [
        { from: 'Johannesburg (JNB)', to: 'Paris (CDG)', transit: '11-13 hours' },
        { from: 'Cape Town (CPT)', to: 'London (LHR)', transit: '11-13 hours' },
        { from: 'Johannesburg (JNB)', to: 'Frankfurt (FRA)', transit: '10-12 hours' }
      ],
      oceanRoutes: [
        { from: 'Durban', to: 'Le Havre', transit: '18-22 days' },
        { from: 'Cape Town', to: 'Marseille', transit: '16-20 days' },
        { from: 'Ngqura', to: 'Rotterdam', transit: '18-22 days' }
      ]
    },
    {
      origin: 'Africa',
      destination: 'Europe',
      description: 'Freight coordination across major African trade routes to European destinations.',
      airRoutes: [
        { from: 'Nairobi (NBO)', to: 'Amsterdam (AMS)', transit: '8-10 hours' },
        { from: 'Lagos (LOS)', to: 'Paris (CDG)', transit: '6-8 hours' },
        { from: 'Accra (ACC)', to: 'London (LHR)', transit: '6-7 hours' }
      ],
      oceanRoutes: [
        { from: 'Mombasa', to: 'Rotterdam', transit: '20-25 days' },
        { from: 'Lagos', to: 'Le Havre', transit: '15-18 days' },
        { from: 'Tema', to: 'Antwerp', transit: '14-17 days' }
      ]
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="trade-routes-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Trade Routes</span>
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase mt-4 mb-6"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Europe–Africa Trade Corridors
            </h1>
            <p className="text-xl leading-relaxed text-slate-600 mb-12">
              NETEXEC GLOBAL operates across established air and ocean freight routes connecting South Africa, Europe, and Africa. Our route expertise ensures efficient cargo movement through major trade corridors.
            </p>

            {/* Trade Routes Map */}
            <div className="mt-12 rounded-sm overflow-hidden border border-slate-200 shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-forward-sa/artifacts/37lgm619_Screenshot%202026-04-13%20at%2011.44.19%E2%80%AFAM.png"
                alt="NETEXEC GLOBAL Trade Routes Map showing air and ocean freight connections between South Africa, Europe, and Africa"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Routes */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24">
          {routes.map((route, index) => (
            <div key={index} data-testid={`route-${index}`}>
              <div className="mb-12">
                <div className="flex items-center gap-4 mb-4">
                  <MapPin className="w-6 h-6 text-orange-500" />
                  <h2 
                    className="text-3xl md:text-5xl font-bold tracking-tight uppercase"
                    style={{fontFamily: 'Barlow Condensed, sans-serif'}}
                  >
                    {route.origin} → {route.destination}
                  </h2>
                </div>
                <p className="text-lg text-slate-600">{route.description}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Air Freight */}
                <div className="bg-slate-50 border border-slate-200 p-8 rounded-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-sm flex items-center justify-center">
                      <Plane className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-semibold text-slate-900">Air Freight Routes</h3>
                  </div>
                  <div className="space-y-4">
                    {route.airRoutes.map((airRoute, airIndex) => (
                      <div key={airIndex} className="bg-white p-4 rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{airRoute.from}</span>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{airRoute.to}</span>
                        </div>
                        <span className="text-sm text-slate-600">Transit: {airRoute.transit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ocean Freight */}
                <div className="bg-slate-50 border border-slate-200 p-8 rounded-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-orange-50 text-orange-500 rounded-sm flex items-center justify-center">
                      <Ship className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-semibold text-slate-900">Ocean Freight Routes</h3>
                  </div>
                  <div className="space-y-4">
                    {route.oceanRoutes.map((oceanRoute, oceanIndex) => (
                      <div key={oceanIndex} className="bg-white p-4 rounded-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900">{oceanRoute.from}</span>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{oceanRoute.to}</span>
                        </div>
                        <span className="text-sm text-slate-600">Transit: {oceanRoute.transit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Need Freight on These Routes?
          </h2>
          <p className="text-lg leading-relaxed text-slate-300 mb-8">
            Contact NETEXEC GLOBAL for customized freight solutions across Europe–Africa trade corridors.
          </p>
          <Link
            to="/contact"
            data-testid="routes-cta-btn"
            className="bg-orange-500 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-600 transition-colors duration-300 inline-flex items-center gap-2"
          >
            Request a Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TradeRoutes;